export default function dateFormatter(date: string) {
  if (!date) return new Date()
  let parts = date.replace(' ', 'T').split(/[-T:.Z]/)
  if (parts.length < 6) {
    return new Date()
  }
  return new Date(
    Date.UTC(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10),
      parseInt(parts[3], 10),
      parseInt(parts[4], 10),
      parseInt(parts[5], 10)
    )
  )
}

/**
 * The backoffice MySQL session runs in US/Eastern (SELECT @@session.time_zone), so DATETIME
 * columns (issue_date, created_at, due_date, issued_at, ...) are stored as US/Eastern wall-clock,
 * NOT UTC. dateFormatter() above wrongly parses the stored string as UTC, so after a further
 * toLocaleString() conversion the displayed time is ~4-5h off for EU viewers. For money documents
 * we instead interpret the stored value in America/New_York (DST-aware) to recover the true
 * instant; callers then render it in the viewer's local timezone via toLocaleString() as before.
 */
const DB_TIME_ZONE = 'America/New_York'

// Offset (ms) between the given zone's wall-clock and UTC at a specific instant.
function zoneOffsetMs(timeZone: string, date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const p: Record<string, string> = {}
  for (const part of dtf.formatToParts(date)) p[part.type] = part.value
  const asUTC = Date.UTC(
    parseInt(p.year, 10),
    parseInt(p.month, 10) - 1,
    parseInt(p.day, 10),
    parseInt(p.hour, 10),
    parseInt(p.minute, 10),
    parseInt(p.second, 10)
  )
  return asUTC - date.getTime()
}

/**
 * Parse a US/Eastern DATETIME string from the backoffice DB into the correct instant (a JS Date).
 * Use this instead of dateFormatter() for any value read from the invoices/payments/subscriptions
 * tables, so toLocaleString() renders the viewer's real local time.
 */
export function easternDbToDate(date: string): Date {
  if (!date) return new Date()
  let parts = date.replace(' ', 'T').split(/[-T:.Z]/)
  if (parts.length < 6) {
    return new Date()
  }
  // Interpret the wall-clock numbers as if UTC first...
  const naiveUTC = Date.UTC(
    parseInt(parts[0], 10),
    parseInt(parts[1], 10) - 1,
    parseInt(parts[2], 10),
    parseInt(parts[3], 10),
    parseInt(parts[4], 10),
    parseInt(parts[5], 10)
  )
  // ...then shift by the US/Eastern offset that applies at that moment to get the true instant.
  const offset = zoneOffsetMs(DB_TIME_ZONE, new Date(naiveUTC))
  return new Date(naiveUTC - offset)
}

export function dateFormatterNoUTC(date: string) {
  if (!date) return new Date()

  let parts = date.replace(' ', 'T').split(/[-T:.Z]/)
  if (parts.length < 6) {
    return new Date()
  }

  return new Date(
    parseInt(parts[0], 10), // Год
    parseInt(parts[1], 10) - 1, // Месяц (0-11)
    parseInt(parts[2], 10), // День
    parseInt(parts[3], 10), // Часы
    parseInt(parts[4], 10), // Минуты
    parseInt(parts[5], 10) // Секунды
  )
}

/**
 * Formats subscription validity as a month range based on created_at and expiration_date
 * Example: Created Jan 29, Expires Jul 29 -> "Jan - Jun 2024"
 * The subscription is valid for the months it covers (not including expiration month)
 */
export function formatSubscriptionPeriod(
  createdAt: string,
  expirationDate: string,
  locale: string = 'en'
): string {
  if (!createdAt || !expirationDate) return ''

  const startDate = dateFormatter(createdAt)
  const expDate = dateFormatter(expirationDate)

  if (isNaN(startDate.getTime()) || isNaN(expDate.getTime())) return ''

  // End date is the month before expiration (since expiration is the first day subscription is invalid)
  // If expires Jul 29, last valid month is June
  // Set day to 1 first to avoid month overflow (e.g. Mar 29 -> Feb 29 doesn't exist in non-leap years)
  const endDate = new Date(expDate)
  endDate.setDate(1)
  endDate.setMonth(endDate.getMonth() - 1)

  const monthFormat: Intl.DateTimeFormatOptions = {month: 'short'}
  const startMonth = startDate.toLocaleDateString(locale, monthFormat)
  const endMonth = endDate.toLocaleDateString(locale, monthFormat)
  const startYear = startDate.getFullYear()
  const endYear = endDate.getFullYear()

  // If same month and year, just show single month: "Jan 2024"
  if (startYear === endYear && startDate.getMonth() === endDate.getMonth()) {
    return `${startMonth} ${startYear}`
  }

  // If same year, show: "Jan - Jun 2024"
  // If different years, show: "Nov 2024 - Apr 2025"
  if (startYear === endYear) {
    return `${startMonth} - ${endMonth} ${endYear}`
  } else {
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`
  }
}
